import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import Layout, { Empty } from '@/components/Layout'
import { Badge, Button, Chip, Field, List, Row, Section, fieldInputClass } from '@/components/ui'
import { Sheet, SheetPad } from '@/components/Sheet'
import { toast } from '@/components/Toast'
import { useCanWrite } from '@/lib/access'
import type { RewardFull } from '@/types'
import { t } from '@/lib/i18n'

// 兑换记录用的是 LedgerEntry#as_json_row 的形状，这里只取这一页会渲染的字段。
interface RedemptionRow {
  id: number
  note: string | null
  delta: number
  at: string
  fulfilled: boolean
}

// 稿子里加奖励的分值是五个 chip + 一个「或者自己填一个」。
const COSTS = [20, 30, 50, 100, 200]

function fmtTime(iso: string) {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export default function Index({ rewards, balance, redemptions, spent_total }: {
  rewards: RewardFull[]
  balance: number
  redemptions: RedemptionRow[]
  spent_total: number
}) {
  const canWrite = useCanWrite()

  const [buying, setBuying] = useState<RewardFull | null>(null)
  const [acting, setActing] = useState<RewardFull | null>(null)
  const [deleting, setDeleting] = useState<RewardFull | null>(null)
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [cost, setCost] = useState('')
  const [stock, setStock] = useState('')

  // onSuccess 回调里要读**刷新后**的 props，而闭包捕获的是刷新前那一份。
  // 用 ref 追最新值：每次渲染写一次，回调里读 .current 就是最新的。
  const latest = useRef(redemptions)
  latest.current = redemptions

  const confirmBuy = () => {
    const r = buying!
    setBuying(null)
    router.post(`/rewards/${r.id}/redeem`, {}, {
      onSuccess: () => toast(
        t('store.toast_redeemed', { title: r.title, count: r.cost }),
        {
          undo: () => {
            // 刚刚那一条就是刷新后 redemptions 的第一条（recent_first）。
            const just = latest.current[0]
            if (just) {
              router.delete(`/redemptions/${just.id}`, {
                onSuccess: () => toast(t('store.toast_refunded', { title: r.title })),
              })
            }
          },
        },
      ),
    })
  }

  const saveReward = () => {
    router.post('/rewards', { title, cost, stock }, {
      onSuccess: () => {
        setAdding(false)
        toast(t('store.toast_added', { title }))
        setTitle(''); setCost(''); setStock('')
      },
    })
  }

  const doDelete = () => {
    const r = deleting!
    setDeleting(null)
    router.delete(`/rewards/${r.id}`, { onSuccess: () => toast(t('store.toast_deleted', { title: r.title })) })
  }

  const fulfill = (row: RedemptionRow) => {
    router.post(`/redemptions/${row.id}/fulfill`, {}, {
      preserveScroll: true,
      onSuccess: () => toast(t('store.toast_given')),
    })
  }

  // 「够不够」是这一页唯一的问题，分组直接把它答了。
  const afford = rewards.filter((r) => balance >= r.cost && !r.out_of_stock)
  const locked = rewards.filter((r) => balance < r.cost || r.out_of_stock)

  // 每行恒定两个可点区：**行本体 = 兑换**（规矩 §4② 整行可点），右侧一个 44px 的 ⋯。
  // 稿子那个 72px 的「兑换」按钮省掉了 —— 再加一个就是第三个，375px 上会把奖励名
  // 挤成两个字一行（稿子自己在作业页那一节写过这条）。
  const moreButton = (r: RewardFull) => canWrite ? (
    <button
      type="button"
      onClick={() => setActing(r)}
      aria-label={t('store.more_aria', { title: r.title })}
      aria-haspopup="dialog"
      className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-lg active:bg-sunk"
    >
      ⋯
    </button>
  ) : undefined

  return (
    <Layout bleed>
      <section className="mx-3 mt-3 p-[18px] rounded-card text-white bg-gradient-to-br from-brand to-brand2 shadow-hero">
        <div className="text-[13px] font-semibold opacity-90">💰 {t('store.balance_label')}</div>
        <p className="m-0 mt-0.5 text-[42px] font-extrabold leading-[1.05]">{balance}</p>
        <p className="m-0 mt-2.5 text-xs opacity-85">
          {redemptions.length > 0
            ? t('store.balance_note_spent', { count: redemptions.length, points: spent_total })
            : t('store.balance_note_empty')}
        </p>
      </section>

      {rewards.length === 0 && (
        <Empty
          emoji="🛍️"
          title={t('store.empty_title')}
          action={canWrite ? <Button variant="ghost" onClick={() => setAdding(true)}>＋ {t('store.add')}</Button> : undefined}
        >
          {t('store.empty')}
        </Empty>
      )}

      {afford.length > 0 && (
        <Section
          title={t('store.affordable', { count: afford.length })}
          tail={canWrite ? (
            <button type="button" onClick={() => setAdding(true)} className="min-h-tap px-2 text-brand font-bold text-[13px]">
              ＋ {t('store.add')}
            </button>
          ) : undefined}
        >
          <List>
            {afford.map((r) => (
              <Row
                key={r.id}
                lead="🎁"
                title={r.title}
                sub={t('store.cost_and_left', { cost: r.cost, left: balance - r.cost })}
                pts={t('store.points', { count: r.cost })}
                onTap={canWrite ? () => setBuying(r) : undefined}
                ariaLabel={t('store.redeem_aria', { title: r.title, count: r.cost })}
                right={moreButton(r)}
              />
            ))}
          </List>
        </Section>
      )}

      {locked.length > 0 && (
        <Section title={t('store.saving_up', { count: locked.length })}>
          <List>
            {locked.map((r) => (
              // 换不起的行**不给按钮**：给一个点了会被拒的按钮，是在制造一次失败。
              // 换成一条进度条 + 「还差 N 分」，够不够一眼看得出。
              <Row
                key={r.id}
                lead="🔒"
                title={r.title}
                sub={
                  <span className="block">
                    <span>
                      {t('store.points', { count: r.cost })}
                      {r.stock != null && ` · ${t('store.stock_left', { count: r.stock })}`}
                    </span>
                    <span className="block h-[5px] mt-[7px] rounded-full bg-sunk overflow-hidden">
                      <span
                        className="block h-full rounded-full bg-faint"
                        style={{ width: `${Math.min(100, Math.round((balance / r.cost) * 100))}%` }}
                      />
                    </span>
                  </span>
                }
                pts={
                  <span className="text-faint">
                    {r.out_of_stock ? t('store.sold_out') : t('store.short_by', { count: r.cost - balance })}
                  </span>
                }
                right={moreButton(r)}
              />
            ))}
          </List>
        </Section>
      )}

      {redemptions.length > 0 && (
        <Section title={t('store.history')}>
          <List>
            {redemptions.map((row) => (
              <Row
                key={row.id}
                lead="🧾"
                title={row.note}
                sub={t('store.spent_on', { points: Math.abs(row.delta), at: fmtTime(row.at) })}
                right={
                  row.fulfilled
                    ? <Badge tone="green">✓ {t('store.given')}</Badge>
                    : canWrite
                      ? (
                        <button
                          type="button"
                          onClick={() => fulfill(row)}
                          className="shrink-0 min-h-tap px-3 rounded-chip bg-amber-tint text-amber-strong font-bold text-xs"
                        >
                          {t('store.mark_given')}
                        </button>
                      )
                      : <Badge tone="amber">⏳ {t('store.owed_short')}</Badge>
                }
              />
            ))}
          </List>
          <p className="mt-2 px-1 text-faint text-xs">{t('store.pending_hint')}</p>
        </Section>
      )}

      {/* 兑换确认：花分是感受上不可逆的动作，而「换完还剩多少」是人真正在算的那个数。 */}
      <Sheet open={buying !== null} onClose={() => setBuying(null)} label={t('store.confirm_title')}>
        <div className="px-5 pt-1 text-center">
          <div className="text-[52px] leading-none" aria-hidden="true">🎁</div>
          <h4 className="mt-2 mb-1 text-lg font-bold">{buying?.title}</h4>
          <p className="m-0 mb-4 text-muted text-[13px]">{t('store.confirm_hint')}</p>
        </div>
        <SheetPad>
          <div className="flex justify-between py-[11px] border-t border-line text-sm">
            <span>{t('store.math_now')}</span>
            <b className="font-extrabold">{t('store.points', { count: balance })}</b>
          </div>
          <div className="flex justify-between py-[11px] border-t border-line text-sm">
            <span>{t('store.math_cost')}</span>
            <b className="font-extrabold text-red">−{t('store.points', { count: buying?.cost ?? 0 })}</b>
          </div>
          <div className="flex justify-between py-[11px] border-y border-line text-sm">
            <span>{t('store.math_after')}</span>
            <b className="font-extrabold text-brand">{t('store.points', { count: balance - (buying?.cost ?? 0) })}</b>
          </div>
          <div className="grid gap-2 mt-4">
            <Button onClick={confirmBuy}>{t('store.confirm_submit')}</Button>
            <Button variant="plain" onClick={() => setBuying(null)}>{t('store.confirm_cancel')}</Button>
          </div>
        </SheetPad>
      </Sheet>

      {/* 奖励操作。眼下只有删除 —— 改名 / 改分值需要一条 PATCH /rewards/:id，
          而仓库今天没有那条路由（resources :rewards only [create destroy]），
          在一次改版式的切片里顺手加写接口是把两件事混在一起。 */}
      <Sheet open={acting !== null} onClose={() => setActing(null)} label={t('store.actions')} title={acting?.title ?? ''}>
        <div className="px-5 pb-1">
          <Button
            variant="plain"
            onClick={() => { const r = acting!; setActing(null); setDeleting(r) }}
          >
            🗑️ {t('store.delete')}
          </Button>
        </div>
      </Sheet>

      <Sheet
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        label={t('store.delete')}
        title={t('store.delete_title', { title: deleting?.title ?? '' })}
      >
        <div className="mx-5 mb-3.5 px-3.5 py-3 rounded-ctl bg-red-tint text-red-strong text-[13px] leading-[1.45]">
          {t('store.delete_warn')}
        </div>
        <SheetPad>
          <div className="grid gap-2">
            <Button variant="danger" onClick={doDelete}>{t('store.delete_confirm_btn')}</Button>
            <Button variant="plain" onClick={() => setDeleting(null)}>{t('people.keep')}</Button>
          </div>
        </SheetPad>
      </Sheet>

      <Sheet open={adding} onClose={() => setAdding(false)} label={t('store.add')} title={t('store.add')}>
        <SheetPad>
          <Field label={t('store.reward_name')}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('store.reward_placeholder')}
              enterKeyHint="done"
              autoComplete="off"
              className={fieldInputClass}
            />
          </Field>
          <Field label={t('store.cost_label')}>
            <div className="flex gap-2 flex-wrap">
              {COSTS.map((c) => (
                <Chip key={c} pressed={cost === String(c)} onClick={() => setCost(String(c))}>{c}</Chip>
              ))}
            </div>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder={t('store.cost_custom')}
              className={fieldInputClass + ' mt-2'}
            />
          </Field>
          {/* 库存：稿子里没有这一项（原型的奖励没有库存概念），但仓库有 rewards.stock
              且商店页一直在用它。做丢等于功能回退，所以放进来当第三个字段，选填。 */}
          <Field label={t('store.stock_label')}>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              placeholder={t('store.stock_placeholder')}
              className={fieldInputClass}
            />
          </Field>
          <Button disabled={!title.trim() || !Number(cost)} onClick={saveReward}>{t('store.save')}</Button>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}
